import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, FileText, FolderDown, MoreVertical, Plus, Search } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcn/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/shadcn/dropdown-menu';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcn/tooltip.tsx';
import { formatDate3 } from '@/utils';
import { deleteGovernmentIntake } from '../../api/mutations';
import {
  createSystemCatalog,
  getGovernmentIntakeList,
  getSystemCatalogList,
  getSystemPendingList,
  patchSystemCatalog,
  patchSystemPending,
} from '../../api/queries';
import type { GovernmentIntakeListItem, Root } from '../../api/types';
import { AdminDetailView } from './AdminDetailView';

/** Backend list returns intake records with optional payload (Root); or payload-only array. */
type IntakeListItem = GovernmentIntakeListItem | { payload?: Root } | Root;
type PatchCatalogPayload = Parameters<typeof patchSystemCatalog>[1];
type PatchPendingPayload = Parameters<typeof patchSystemPending>[1];

const discoveryQueryKey = ['government-intake', 'admin', 'discovery'] as const;
const catalogQueryKey = ['government-intake', 'admin', 'catalog'] as const;
const pendingQueryKey = ['government-intake', 'admin', 'pending'] as const;

type DiscoveryFile = {
  id: string; // Display ID (index-based)
  apiId: number | string; // Actual API ID for fetching details
  fileName: string;
  jurisdiction: string;
  cityOrAgencyName: string;
  state: string;
  submissionCount: number;
  departments: string[];
  objectFocus: string[];
  reportingNeeded: string;
  formsNeeded: string;
  legacyDataIngestion: string;
  lastUpdated: string;
  status: string;
};

// Helper to map API response into discovery file list shape
const mapIntakeToDiscoveryFile = (
  intake: Root,
  index: number,
  apiId: number | string,
  meta?: {
    status: string | undefined;
    cityOrAgencyName: string | undefined;
    state: string | undefined;
    department: string | undefined;
    lastUpdated: string | undefined;
    objectFocus?: string[] | undefined;
  }
): DiscoveryFile => {
  // Use objectFocus from API response (array), capitalize each item
  let objectFocus: DiscoveryFile['objectFocus'] = ['Properties'];
  if (meta?.objectFocus && meta.objectFocus.length > 0) {
    // Capitalize first letter of each item
    objectFocus = meta.objectFocus.map((item) => item.charAt(0).toUpperCase() + item.slice(1));
  } else {
    // Fallback: infer from primaryObjectOfWork if objectFocus not available
    const primary = intake.primaryObjectOfWork?.[0];
    if (primary?.programs) objectFocus = ['Programs'];
    else if (primary?.projects) objectFocus = ['Projects'];
    else if (primary?.properties) objectFocus = ['Properties'];
  }

  // Prefer flattened metadata from the list item (outside payload), falling back to payload entryContact.
  const city = meta?.cityOrAgencyName ?? intake.entryContact?.cityOrAgencyName ?? '';
  const state = meta?.state ?? intake.entryContact?.state ?? '';
  const department = meta?.department ?? intake.entryContact?.department ?? '';
  const _updateAt = formatDate3(meta?.lastUpdated ?? '', 'yyyy-mm-dd');

  // Aggregate flags from the primary objects of work
  let _legacyData = '';
  let _reportingNeeded = '';
  let _formsNeeded = '';

  for (const obj of intake.primaryObjectOfWork ?? []) {
    const programs = obj.programs?.scoping;
    const projects = obj.projects?.scoping;
    const propertiesNeeds = obj.properties?.needs;
    const propertiesLegacy = obj.properties?.legacyConsolidation;

    // Reporting
    if (programs?.automatedReporting === 'Yes' || projects?.automatedReporting === 'Yes' || propertiesNeeds?.automatedReporting === 'Yes') {
      _reportingNeeded = 'Yes';
    }

    // Program Forms (count-based)
    const programFormsCount =
      (programs?.programForms === 'Yes' ? 1 : 0) +
      (projects?.programForms === 'Yes' ? 1 : 0) +
      (propertiesNeeds?.programForms === 'Yes' ? 1 : 0);

    if (programFormsCount === 3) {
      _formsNeeded = 'All';
    } else if (programFormsCount === 2) {
      _formsNeeded = _formsNeeded === 'All' ? 'All' : 'Both';
    } else if (programFormsCount === 1 && !_formsNeeded) {
      _formsNeeded = 'Yes';
    }

    // Data Consolidation
    if (programs?.consolidate === 'Yes' || projects?.consolidate === 'Yes' || propertiesLegacy?.consolidate === 'Yes') {
      _legacyData = 'Yes';
    }

    if (_reportingNeeded === 'Yes' && _formsNeeded === 'All' && _legacyData === 'Yes') {
      break;
    }
  }

  // Map API status to display status
  const displayStatus = 'New';
  //if (meta?.status) {
  //  if (meta.status === 'SUBMITTED') displayStatus = 'In Review';
  //  else if (meta.status === 'APPROVED' || meta.status === 'READY') displayStatus = 'Ready';
  //  else displayStatus = meta.status;
  //}

  return {
    id: String(index + 1),
    apiId,
    fileName: city ? `${city} Discovery` : 'Government Intake Discovery',
    jurisdiction: city && state ? `${state}` : city || '—',
    cityOrAgencyName: city,
    state,
    submissionCount: 1,
    departments: department ? [department] : [],
    objectFocus,
    reportingNeeded: _reportingNeeded || '',
    formsNeeded: _formsNeeded || '',
    legacyDataIngestion: _legacyData || '',
    lastUpdated: _updateAt || '',
    status: displayStatus,
  };
};

type CatalogItem = {
  id: string;
  name: string;
  vendor?: string;
  category?: string;
  aliases?: string[];
  isActive: boolean;
};

type PendingItem = {
  id: string;
  submittedName: string;
  submittedVendor?: string;
  submittedNotes?: string;
  status: 'pending' | 'approved' | 'merged' | 'rejected';
  approvedCatalogId?: string;
  resolutionNotes?: string;
};

const mapDiscoveryList = (list: IntakeListItem[]): DiscoveryFile[] => {
  return list.map((item, index) => {
    let payload: Root;
    let apiId: number | string = index + 1;
    let itemStatus: string | undefined;
    let cityOrAgencyName: string | undefined;
    let state: string | undefined;
    let department: string | undefined;
    let lastUpdated: string | undefined;
    let objectFocus: string[] | undefined;

    if (item && typeof item === 'object') {
      if ('payload' in item && item.payload != null) {
        payload = item.payload;
        if ('id' in item && item.id != null) {
          apiId = item.id;
        }
        if ('status' in item) {
          itemStatus = item.status;
        }
        if ('cityOrAgencyName' in item) {
          cityOrAgencyName = (item as GovernmentIntakeListItem).cityOrAgencyName;
        }
        if ('state' in item) {
          state = (item as GovernmentIntakeListItem).state;
        }
        if ('department' in item) {
          department = (item as GovernmentIntakeListItem).department;
        }
        if ('updatedAt' in item) {
          lastUpdated = (item as GovernmentIntakeListItem).updatedAt;
        }
        if ('objectFocus' in item && Array.isArray(item.objectFocus)) {
          objectFocus = item.objectFocus as string[];
        }
      } else {
        payload = item as Root;
      }
    } else {
      payload = item as Root;
    }

    return mapIntakeToDiscoveryFile(payload, index, apiId, {
      status: itemStatus,
      cityOrAgencyName,
      state,
      department,
      lastUpdated,
      objectFocus,
    });
  });
};

const mapCatalogList = (rows: unknown[]): CatalogItem[] => {
  return rows
    .filter(
      (
        row
      ): row is {
        id: number | string;
        name: string;
        vendor?: string;
        category?: string;
        aliases?: unknown;
        isActive: boolean;
      } => {
        return row != null && typeof row === 'object' && 'id' in row && 'name' in row && 'isActive' in row;
      }
    )
    .map((row) => ({
      id: String(row.id),
      name: row.name,
      vendor: row.vendor ?? undefined,
      category: row.category ?? undefined,
      aliases: Array.isArray(row.aliases) ? (row.aliases as string[]) : [],
      isActive: row.isActive,
    }));
};

const mapPendingList = (rows: unknown[]): PendingItem[] => {
  return rows
    .filter(
      (
        row
      ): row is {
        id: number | string;
        submittedName: string;
        submittedVendor?: string;
        submittedNotes?: string;
        status: PendingItem['status'];
        approvedCatalogId?: number | string | null;
        resolutionNotes?: string;
      } => {
        return row != null && typeof row === 'object' && 'id' in row && 'submittedName' in row && 'status' in row;
      }
    )
    .map((row) => ({
      id: String(row.id),
      submittedName: row.submittedName,
      submittedVendor: row.submittedVendor ?? undefined,
      submittedNotes: row.submittedNotes ?? undefined,
      status: row.status,
      approvedCatalogId: row.approvedCatalogId != null ? String(row.approvedCatalogId) : undefined,
      resolutionNotes: row.resolutionNotes ?? undefined,
    }));
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === 'object') {
    const candidate = error as { response?: { data?: { message?: unknown } }; message?: unknown };
    if (candidate.response?.data?.message != null) return String(candidate.response.data.message);
    if (candidate.message != null) return String(candidate.message);
  }
  return fallback;
};

export function AdminListUI() {
  const queryClient = useQueryClient();
  const id = useId();

  const [activeTab, setActiveTab] = useState('discovery');

  // Discovery Files state
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<DiscoveryFile | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [objectFilter, setObjectFilter] = useState<string>('all');

  // System Catalog UI state
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState('all');
  const [isAddSystemOpen, setIsAddSystemOpen] = useState(false);
  const [addSystemError, setAddSystemError] = useState<string | null>(null);
  const [isEditSystemOpen, setIsEditSystemOpen] = useState(false);
  const [editingSystem, setEditingSystem] = useState<CatalogItem | null>(null);
  const [newSystem, setNewSystem] = useState<Partial<CatalogItem>>({ name: '', vendor: '', category: '', aliases: [], isActive: true });

  // Pending Additions UI state
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingStatusFilter, setPendingStatusFilter] = useState('all');
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [selectedPending, setSelectedPending] = useState<PendingItem | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const discoveryQuery = useQuery({
    queryKey: discoveryQueryKey,
    queryFn: () => getGovernmentIntakeList(),
  });

  const catalogQuery = useQuery({
    queryKey: catalogQueryKey,
    queryFn: () => getSystemCatalogList(),
    enabled: activeTab === 'catalog' || activeTab === 'pending',
  });

  const pendingQuery = useQuery({
    queryKey: pendingQueryKey,
    queryFn: () => getSystemPendingList(),
    enabled: activeTab === 'pending',
  });

  const listItems = useMemo<IntakeListItem[]>(() => {
    const data = discoveryQuery.data?.data;
    return Array.isArray(data) ? (data as IntakeListItem[]) : [];
  }, [discoveryQuery.data]);

  const files = useMemo(() => mapDiscoveryList(listItems), [listItems]);
  const isLoadingFiles = discoveryQuery.isLoading;
  const filesError = discoveryQuery.isError ? 'Unable to load discovery files. Please try again.' : null;

  const catalogData = useMemo(() => {
    const data = catalogQuery.data?.data;
    return mapCatalogList(Array.isArray(data) ? data : []);
  }, [catalogQuery.data]);
  const isLoadingCatalog = catalogQuery.isLoading;
  const catalogError = catalogQuery.isError ? 'Unable to load system catalog. Please try again.' : null;

  const pendingData = useMemo(() => {
    const data = pendingQuery.data?.data;
    return mapPendingList(Array.isArray(data) ? data : []);
  }, [pendingQuery.data]);
  const isLoadingPending = pendingQuery.isLoading;
  const pendingError = pendingQuery.isError ? 'Unable to load pending additions. Please try again.' : null;

  // Discovery Files filtering
  const filteredFiles = files.filter((file) => {
    if (statusFilter !== 'all' && file.status !== statusFilter) return false;
    if (objectFilter !== 'all') {
      // Check if any item in objectFocus array matches the filter (case-insensitive)
      const filterLower = objectFilter.toLowerCase();
      return file.objectFocus.some((focus) => focus.toLowerCase() === filterLower);
    }
    return true;
  });

  // Merge rows when cityOrAgencyName and state are exactly the same; show one row with multiple departments
  const mergedFiles = useMemo(() => {
    const byKey = new Map<string, DiscoveryFile>();

    for (const file of filteredFiles) {
      const key = `${file.cityOrAgencyName}__${file.state}`;
      const existing = byKey.get(key);

      if (!existing) {
        byKey.set(key, { ...file });
      } else {
        // Merge departments (deduplicated)
        const mergedDepartments = Array.from(new Set([...existing.departments, ...file.departments]));
        existing.departments = mergedDepartments;

        existing.submissionCount += file.submissionCount;
      }
    }

    return Array.from(byKey.values());
  }, [filteredFiles]);

  const refreshCatalog = async () => {
    await queryClient.invalidateQueries({ queryKey: catalogQueryKey });
  };

  const refreshPending = async () => {
    await queryClient.invalidateQueries({ queryKey: pendingQueryKey });
  };

  const deleteIntakeMutation = useMutation({
    mutationFn: (intakeId: string) => deleteGovernmentIntake({ intakeId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discoveryQueryKey });
      setFileToDelete(null);
    },
  });

  const addCatalogMutation = useMutation({
    mutationFn: createSystemCatalog,
  });

  const addCatalogFromPendingMutation = useMutation({
    mutationFn: createSystemCatalog,
  });

  const patchCatalogMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PatchCatalogPayload }) => patchSystemCatalog(id, payload),
  });

  const patchPendingMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PatchPendingPayload }) => patchSystemPending(id, payload),
  });

  // Catalog filtering
  const filteredCatalog = catalogData.filter((item) => {
    const matchesSearch =
      item.name.toLowerCase().includes(catalogSearch.toLowerCase()) || item.vendor?.toLowerCase().includes(catalogSearch.toLowerCase());
    const matchesCategory = catalogCategoryFilter === 'all' || item.category === catalogCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Pending filtering
  const filteredPending = pendingData.filter((item) => {
    const matchesSearch =
      item.submittedName.toLowerCase().includes(pendingSearch.toLowerCase()) ||
      item.submittedVendor?.toLowerCase().includes(pendingSearch.toLowerCase());
    const matchesStatus = pendingStatusFilter === 'all' || item.status === pendingStatusFilter;
    return matchesSearch && matchesStatus;
  });

  if (selectedFileId !== null) {
    const selected = files.find((f) => f.id === selectedFileId);
    const fullList = listItems;
    // Pass only list items that belong to the clicked row (same city + state as the selected file)
    const rowListItems =
      selected && fullList.length > 0 && files.length === fullList.length
        ? fullList.filter((_: IntakeListItem, index: number) => {
            const f = files[index];
            return f && f.cityOrAgencyName === selected.cityOrAgencyName && f.state === selected.state;
          })
        : fullList;

    if (!selected) {
      return (
        <AdminDetailView
          fileId={selectedFileId}
          apiId={selectedFileId}
          file={{
            id: selectedFileId,
            apiId: selectedFileId,
            fileName: '',
            jurisdiction: '',
            cityOrAgencyName: '',
            state: '',
            submissionCount: 0,
            departments: [],
            objectFocus: [],
            reportingNeeded: '',
            formsNeeded: '',
            legacyDataIngestion: '',
            lastUpdated: '',
            status: '',
          }}
          listItems={[]}
          onBack={() => setSelectedFileId(null)}
        />
      );
    }

    return (
      <AdminDetailView
        fileId={selectedFileId}
        apiId={String(selected.apiId)}
        file={selected}
        listItems={rowListItems}
        onBack={() => setSelectedFileId(null)}
      />
    );
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      New: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
      'In Review': 'bg-yellow-500/10 text-yellow-600 border-yellow-400',
      Ready: 'bg-green-500/10 text-green-700 border-green-500/20',
    };
    return variants[status] || 'bg-muted text-muted-foreground';
  };

  const getPendingStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      pending: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20',
      approved: 'bg-green-500/10 text-green-700 border-green-500/20',
      merged: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
      rejected: 'bg-red-500/10 text-red-700 border-red-500/20',
    };
    return variants[status] || 'bg-muted text-muted-foreground';
  };

  const handleAddSystem = async () => {
    if (!newSystem.name?.trim()) return;

    setAddSystemError(null);
    try {
      await addCatalogMutation.mutateAsync({
        name: newSystem.name.trim(),
        vendor: newSystem.vendor?.trim() || undefined,
        category: newSystem.category?.trim() || undefined,
        aliases: newSystem.aliases?.filter(Boolean) || [],
        isActive: newSystem.isActive ?? true,
        // createdById: accountId ?? undefined,
        // updatedById: accountId ?? undefined,
      });
      setNewSystem({ name: '', vendor: '', category: '', aliases: [], isActive: true });
      setIsAddSystemOpen(false);
      await refreshCatalog();
    } catch (err: unknown) {
      setAddSystemError(getErrorMessage(err, 'Failed to add system'));
    }
  };

  const handleEditSystem = async () => {
    if (!editingSystem || !editingSystem.name?.trim()) return;

    try {
      await patchCatalogMutation.mutateAsync({
        id: editingSystem.id,
        payload: {
          name: editingSystem.name.trim(),
          vendor: editingSystem.vendor?.trim() || undefined,
          category: editingSystem.category?.trim() || undefined,
          aliases: editingSystem.aliases?.filter(Boolean) || [],
          isActive: editingSystem.isActive,
        },
      });
      setEditingSystem(null);
      setIsEditSystemOpen(false);
      await refreshCatalog();
    } catch (err) {
      console.error('Failed to update system catalog entry.', err);
    }
  };

  const handleToggleActive = async (id: string) => {
    const existing = catalogData.find((item) => item.id === id);
    if (!existing) return;

    try {
      await patchCatalogMutation.mutateAsync({
        id,
        payload: { isActive: !existing.isActive },
      });
      await refreshCatalog();
    } catch (err) {
      console.error('Failed to toggle system catalog status.', err);
    }
  };

  const openEditDialog = (system: CatalogItem) => {
    setEditingSystem({ ...system });
    setIsEditSystemOpen(true);
  };

  const handleApprovePending = async (item: PendingItem) => {
    try {
      const catalogResponse = await addCatalogFromPendingMutation.mutateAsync({
        name: item.submittedName,
        vendor: item.submittedVendor || undefined,
        aliases: [],
        isActive: true,
      });

      await patchPendingMutation.mutateAsync({
        id: item.id,
        payload: {
          status: 'approved',
          approvedCatalogId: catalogResponse.data.id,
          resolutionNotes: 'Added to catalog',
        },
      });

      await Promise.all([refreshCatalog(), refreshPending()]);
    } catch (err) {
      console.error('Failed to approve pending system.', err);
    }
  };

  const handleMergePending = async () => {
    if (!selectedPending || !mergeTargetId) return;

    try {
      await patchPendingMutation.mutateAsync({
        id: selectedPending.id,
        payload: {
          status: 'merged',
          approvedCatalogId: Number(mergeTargetId),
          resolutionNotes: 'Merged into existing system',
        },
      });
      setIsMergeDialogOpen(false);
      setSelectedPending(null);
      setMergeTargetId('');
      await refreshPending();
    } catch (err) {
      console.error('Failed to merge pending system.', err);
    }
  };

  const handleRejectPending = async () => {
    if (!selectedPending) return;

    try {
      await patchPendingMutation.mutateAsync({
        id: selectedPending.id,
        payload: {
          status: 'rejected',
          resolutionNotes: rejectReason || 'Rejected',
        },
      });
      setIsRejectDialogOpen(false);
      setSelectedPending(null);
      setRejectReason('');
      await refreshPending();
    } catch (err) {
      console.error('Failed to reject pending system.', err);
    }
  };

  const openMergeDialog = (item: PendingItem) => {
    setSelectedPending(item);
    setIsMergeDialogOpen(true);
  };

  const openRejectDialog = (item: PendingItem) => {
    setSelectedPending(item);
    setIsRejectDialogOpen(true);
  };

  const categories = Array.from(new Set(catalogData.map((item) => item.category).filter(Boolean))) as string[];

  return (
    <div className='space-y-6 px-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-2xl font-semibold text-foreground'>Admin Dashboard</h2>
          <p className='text-sm text-muted-foreground mt-1'>Manage discovery files and system catalog</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className='space-y-6'>
        <TabsList className='grid w-full max-w-lg grid-cols-3 bg-zinc-100 rounded-full'>
          <TabsTrigger className='data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-full' value='discovery'>
            Discovery Files
          </TabsTrigger>
          <TabsTrigger className='data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-full' value='catalog'>
            System Catalog
          </TabsTrigger>
          <TabsTrigger className='data-[state=active]:bg-gray-50 data-[state=active]:text-black rounded-full' value='pending'>
            Pending Additions
          </TabsTrigger>
        </TabsList>

        {/* DISCOVERY FILES TAB */}
        <TabsContent value='discovery' className='space-y-6'>
          <div>
            <h3 className='text-xl font-semibold text-foreground'>Government Discovery Files</h3>
            <p className='text-sm text-muted-foreground mt-1'>Review and aggregate departmental submissions</p>
          </div>

          {filesError && (
            <p className='text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 rounded px-2 py-1 inline-block'>{filesError}</p>
          )}

          {/* Selected file (internal state, no props) */}
          {null}

          {/* Filters */}
          <div className='flex gap-4'>
            <div className='w-48'>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='Filter by status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Statuses</SelectItem>
                  <SelectItem value='New'>New</SelectItem>
                  <SelectItem value='In Review'>In Review</SelectItem>
                  <SelectItem value='Ready'>Ready</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='w-48'>
              <Select value={objectFilter} onValueChange={setObjectFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='Filter by object' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Objects</SelectItem>
                  <SelectItem value='Programs'>Programs</SelectItem>
                  <SelectItem value='Projects'>Projects</SelectItem>
                  <SelectItem value='Properties'>Properties</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Files Table */}
          <div className='border border-gray-300 rounded-lg overflow-hidden'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File Name</TableHead>
                  <TableHead>Jurisdiction</TableHead>
                  <TableHead className='text-center'>Submissions</TableHead>
                  <TableHead>Departments</TableHead>
                  <TableHead>Object Focus</TableHead>
                  <TableHead>Reporting</TableHead>
                  <TableHead>Forms</TableHead>
                  <TableHead>Legacy Data</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingFiles ? (
                  <TableRow>
                    <TableCell colSpan={11} className='text-center py-8 text-muted-foreground'>
                      Loading discovery files...
                    </TableCell>
                  </TableRow>
                ) : filteredFiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className='text-center py-8 text-muted-foreground'>
                      No files found matching the filters
                    </TableCell>
                  </TableRow>
                ) : (
                  mergedFiles.map((file) => {
                    const isSelected = selectedFileId === file.id;
                    return (
                      <TableRow
                        key={file.id}
                        className={`cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/5' + ' ring-1' + ' ring-primary/20' : ''}`}
                        onClick={() => setSelectedFileId((prev) => (prev === file.id ? null : file.id))}
                      >
                        <TableCell className='font-medium'>
                          <div className='flex items-center gap-2'>
                            <FileText className='h-4 w-4 text-gray-600' />
                            {file.fileName}
                          </div>
                        </TableCell>
                        <TableCell className='text-muted-foreground'>{file.jurisdiction}</TableCell>
                        <TableCell className='text-center'>
                          <Badge className='bg-gray-200 text-black'>{file.submissionCount}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className='flex flex-wrap gap-1 max-w-[200px]'>
                            {file.departments.map((dept) => (
                              <Badge key={dept} variant='outline' className='text-xs'>
                                {dept}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className='flex flex-wrap gap-1'>
                            {file.objectFocus.map((focus) => (
                              <Badge key={focus} className='bg-primary/10 text-primary border-primary/20'>
                                {focus}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className='text-muted-foreground text-sm'>{file.reportingNeeded}</TableCell>
                        <TableCell className='text-muted-foreground text-sm'>{file.formsNeeded}</TableCell>
                        <TableCell className='text-muted-foreground text-sm'>{file.legacyDataIngestion}</TableCell>
                        <TableCell className='text-muted-foreground text-sm'>{file.lastUpdated}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadge(file.status)}>{file.status}</Badge>
                        </TableCell>
                        <TableCell className='flex gap-6' onClick={(e) => e.stopPropagation()}>
                          {/*<TooltipProvider>*/}
                          {/*  <Tooltip>*/}
                          {/*    <TooltipTrigger asChild>*/}
                          {/*      <Button*/}
                          {/*          variant="ghost"*/}
                          {/*          size="sm"*/}
                          {/*          onClick={() => setFileToDelete(file)}*/}
                          {/*          className="h-8 w-8 p-0"*/}
                          {/*          aria-label={`Delete ${file.fileName}`}*/}
                          {/*      >*/}
                          {/*        <Delete className="text-red-500 h-4 w-4"/>*/}
                          {/*      </Button>*/}
                          {/*    </TooltipTrigger>*/}
                          {/*    <TooltipContent>*/}
                          {/*      <p className='text-white'>Delete</p>*/}
                          {/*    </TooltipContent>*/}
                          {/*  </Tooltip>*/}
                          {/*</TooltipProvider>*/}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => setFileToDelete(file)}
                                  className='h-8 w-8 p-0'
                                  aria-label={`Delete ${file.fileName}`}
                                >
                                  <FolderDown className='h-4 w-4' />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className='text-white'>{`Archive ${file.fileName}`}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* SYSTEM CATALOG TAB */}
        <TabsContent value='catalog' className='space-y-6'>
          <div>
            <h3 className='text-xl font-semibold text-foreground'>System Catalog</h3>
            <p className='text-sm text-muted-foreground mt-1'>This list powers all system/tool selection across intake forms.</p>
          </div>

          {catalogError && (
            <p className='text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 rounded px-2 py-1 inline-block'>{catalogError}</p>
          )}

          {/* Toolbar */}
          <div className='flex gap-4 items-center'>
            <div className='relative flex-1 max-w-md'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search systems/tools...'
                value={catalogSearch}
                onChange={(e) => setCatalogSearch(e.target.value)}
                className='pl-9'
              />
            </div>
            <div className='w-48'>
              <Select value={catalogCategoryFilter} onValueChange={setCatalogCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='Category' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setIsAddSystemOpen(true)}>
              <Plus className='h-4 w-4 mr-2' />
              Add System
            </Button>
          </div>

          {/* Catalog Table */}
          <div className='border border-border rounded-lg overflow-hidden'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>System Name</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className='w-[60px]'></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingCatalog ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center py-8 text-muted-foreground'>
                      Loading system catalog...
                    </TableCell>
                  </TableRow>
                ) : filteredCatalog.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center py-8 text-muted-foreground'>
                      No systems found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCatalog.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className='font-medium'>{item.name}</TableCell>
                      <TableCell className='text-muted-foreground'>{item.vendor || '—'}</TableCell>
                      <TableCell className='text-muted-foreground'>{item.category || '—'}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            item.isActive
                              ? 'bg-green-500/10 text-green-700 border-green-500/20'
                              : 'bg-muted text-muted-foreground border-border'
                          }
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                              <MoreVertical className='h-4 w-4' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align='end'>
                            <DropdownMenuItem onClick={() => openEditDialog(item)}>Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleActive(item.id)}>
                              {item.isActive ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* PENDING ADDITIONS TAB */}
        <TabsContent value='pending' className='space-y-6'>
          <div>
            <h3 className='text-xl font-semibold text-foreground'>Pending Additions</h3>
            <p className='text-sm text-muted-foreground mt-1'>
              Systems entered as "Other" by users. Review and standardize into the catalog.
            </p>
          </div>

          {pendingError && (
            <p className='text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 rounded px-2 py-1 inline-block'>{pendingError}</p>
          )}

          {/* Toolbar */}
          <div className='flex gap-4 items-center'>
            <div className='relative flex-1 max-w-md'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search pending...'
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
                className='pl-9'
              />
            </div>
            <div className='w-48'>
              <Select value={pendingStatusFilter} onValueChange={setPendingStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder='Status' />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>All</SelectItem>
                  <SelectItem value='pending'>Pending</SelectItem>
                  <SelectItem value='approved'>Approved</SelectItem>
                  <SelectItem value='merged'>Merged</SelectItem>
                  <SelectItem value='rejected'>Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Pending Table */}
          <div className='border border-border rounded-lg overflow-hidden'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Submitted Name</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className='w-[120px]'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingPending ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center py-8 text-muted-foreground'>
                      Loading pending additions...
                    </TableCell>
                  </TableRow>
                ) : filteredPending.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className='text-center py-8 text-muted-foreground'>
                      No pending items found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPending.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className='font-medium'>
                        {/*<FileText className='h-4 w-4 text-gray-600' />*/}
                        {item.submittedName}
                      </TableCell>
                      <TableCell className='text-muted-foreground'>{item.submittedVendor || '—'}</TableCell>
                      <TableCell className='text-muted-foreground text-sm max-w-[200px] truncate'>{item.submittedNotes || '—'}</TableCell>
                      <TableCell>
                        <Badge className={getPendingStatusBadge(item.status)}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.status === 'pending' && (
                          <div className='flex gap-1'>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => handleApprovePending(item)}
                              className='h-8 px-2'
                              title='Approve'
                            >
                              <Check className='h-4 w-4 text-green-600' />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                                  <MoreVertical className='h-4 w-4' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end'>
                                <DropdownMenuItem onClick={() => openMergeDialog(item)}>Merge with existing</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openRejectDialog(item)}>Reject</DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add System Dialog */}
      <Dialog
        open={isAddSystemOpen}
        onOpenChange={(open) => {
          setIsAddSystemOpen(open);
          if (!open) setAddSystemError(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add System</DialogTitle>
            <DialogDescription>Add a new system or tool to the catalog.</DialogDescription>
          </DialogHeader>
          {addSystemError && (
            <p className='text-xs text-yellow-700 bg-yellow-100 border border-yellow-200 rounded px-2 py-1'>{addSystemError}</p>
          )}
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor={`${id}-new-name`}>Name *</Label>
              <Input
                id={`${id}-new-name`}
                value={newSystem.name}
                onChange={(e) => setNewSystem({ ...newSystem, name: e.target.value })}
                placeholder='System name'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${id}-new-vendor`}>Vendor</Label>
              <Input
                id={`${id}-new-vendor`}
                value={newSystem.vendor}
                onChange={(e) => setNewSystem({ ...newSystem, vendor: e.target.value })}
                placeholder='Vendor name'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${id}-new-category`}>Category</Label>
              <Input
                id={`${id}-new-category`}
                value={newSystem.category}
                onChange={(e) => setNewSystem({ ...newSystem, category: e.target.value })}
                placeholder='Category'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor={`${id}-new-aliases`}>Aliases</Label>
              <Input
                id={`${id}-new-aliases`}
                value={newSystem.aliases?.join(', ')}
                onChange={(e) =>
                  setNewSystem({
                    ...newSystem,
                    aliases: e.target.value
                      .split(',')
                      .map((a) => a.trim())
                      .filter(Boolean),
                  })
                }
                placeholder='Comma-separated aliases'
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsAddSystemOpen(false)} disabled={addCatalogMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleAddSystem} disabled={!newSystem.name?.trim() || addCatalogMutation.isPending}>
              {addCatalogMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit System Dialog */}
      <Dialog open={isEditSystemOpen} onOpenChange={setIsEditSystemOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit System</DialogTitle>
            <DialogDescription>Update system information.</DialogDescription>
          </DialogHeader>
          {editingSystem && (
            <div className='space-y-4 py-4'>
              <div className='space-y-2'>
                <Label htmlFor={`${id}-edit-name`}>Name *</Label>
                <Input
                  id={`${id}-edit-name`}
                  value={editingSystem.name}
                  onChange={(e) => setEditingSystem({ ...editingSystem, name: e.target.value })}
                  placeholder='System name'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor={`${id}-edit-vendor`}>Vendor</Label>
                <Input
                  id={`${id}-edit-vendor`}
                  value={editingSystem.vendor || ''}
                  onChange={(e) => setEditingSystem({ ...editingSystem, vendor: e.target.value })}
                  placeholder='Vendor name'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor={`${id}-edit-category`}>Category</Label>
                <Input
                  id={`${id}-edit-category`}
                  value={editingSystem.category || ''}
                  onChange={(e) => setEditingSystem({ ...editingSystem, category: e.target.value })}
                  placeholder='Category'
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor={`${id}-edit-aliases`}>Aliases</Label>
                <Input
                  id={`${id}-edit-aliases`}
                  value={editingSystem.aliases?.join(', ')}
                  onChange={(e) =>
                    setEditingSystem({
                      ...editingSystem,
                      aliases: e.target.value
                        .split(',')
                        .map((a) => a.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder='Comma-separated aliases'
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsEditSystemOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditSystem} disabled={!editingSystem?.name?.trim()}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Dialog */}
      <Dialog open={isMergeDialogOpen} onOpenChange={setIsMergeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge with Existing System</DialogTitle>
            <DialogDescription>Select which catalog system "{selectedPending?.submittedName}" should be merged into.</DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor={`${id}-merge-target`}>Target System</Label>
              <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                <SelectTrigger id={`${id}-merge-target`}>
                  <SelectValue placeholder='Select a system' />
                </SelectTrigger>
                <SelectContent>
                  {catalogData
                    .filter((item) => item.isActive)
                    .map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} {item.vendor ? `(${item.vendor})` : ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMergePending} disabled={!mergeTargetId}>
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Pending Addition</DialogTitle>
            <DialogDescription>Provide a reason for rejecting "{selectedPending?.submittedName}" (optional).</DialogDescription>
          </DialogHeader>
          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor={`${id}-reject-reason`}>Reason</Label>
              <Input
                id={`${id}-reject-reason`}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder='e.g., Not a digital system, duplicate entry...'
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant='outline' onClick={() => setIsRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRejectPending} variant='destructive'>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Discovery File Confirmation Dialog */}
      <Dialog
        open={fileToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setFileToDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Discovery File</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{fileToDelete?.fileName}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant='outline' onClick={() => setFileToDelete(null)} disabled={deleteIntakeMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant='destructive'
              disabled={deleteIntakeMutation.isPending}
              onClick={() => {
                if (fileToDelete) deleteIntakeMutation.mutate(String(fileToDelete.apiId));
              }}
            >
              {deleteIntakeMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
